class AviatorSoundManager {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.engineOsc = null;
    this.engineGain = null;
    this.engineLfo = null;
    this.engineLfoGain = null;
    this.unlocked = false;
    this.unlockHandler = null;
    this.enabled = true;
  }

  ensure() {
    if (typeof window === 'undefined') return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!this.ctx) {
      this.ctx = new AudioContextClass();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.18;
      this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  resume = async () => {
    const ctx = this.ensure();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
      this.unlocked = true;
    } catch (e) {
      // ignore user gesture errors
    }
  };

  attachUnlock() {
    if (typeof window === 'undefined' || this.unlockHandler) return;
    this.unlockHandler = () => this.resume();
    window.addEventListener('pointerdown', this.unlockHandler, { passive: true });
    window.addEventListener('keydown', this.unlockHandler, { passive: true });
    window.addEventListener('touchstart', this.unlockHandler, { passive: true });
  }

  detachUnlock() {
    if (typeof window === 'undefined' || !this.unlockHandler) return;
    window.removeEventListener('pointerdown', this.unlockHandler);
    window.removeEventListener('keydown', this.unlockHandler);
    window.removeEventListener('touchstart', this.unlockHandler);
    this.unlockHandler = null;
  }

  pulse({ frequency = 440, duration = 0.12, type = 'sine', volume = 0.08, attack = 0.01, release = 0.08, frequencyEnd = null }) {
    if (!this.enabled) return;
    const ctx = this.ensure();
    if (!ctx || !this.master || ctx.state === 'suspended') return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, now);
    if (frequencyEnd && Number.isFinite(frequencyEnd)) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(40, frequencyEnd), now + duration);
    }
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + Math.max(attack + 0.02, duration + release));
    osc.connect(gain);
    gain.connect(this.master);
    osc.start(now);
    osc.stop(now + duration + release + 0.05);
  }

  playRoundStart() {
    this.pulse({ frequency: 520, frequencyEnd: 700, duration: 0.12, type: 'triangle', volume: 0.05 });
    window.setTimeout(() => {
      this.pulse({ frequency: 760, frequencyEnd: 930, duration: 0.1, type: 'triangle', volume: 0.04 });
    }, 120);
  }

  playBetPlaced() {
    this.pulse({ frequency: 620, frequencyEnd: 860, duration: 0.09, type: 'square', volume: 0.045 });
    window.setTimeout(() => {
      this.pulse({ frequency: 920, frequencyEnd: 1120, duration: 0.08, type: 'square', volume: 0.035 });
    }, 80);
  }

  playCashout(multiplier = 1) {
    const bonus = Math.min(220, Math.max(0, (Number(multiplier) - 1) * 40));
    this.pulse({ frequency: 740 + bonus, frequencyEnd: 1040 + bonus, duration: 0.12, type: 'triangle', volume: 0.06 });
    window.setTimeout(() => {
      this.pulse({ frequency: 1040 + bonus, frequencyEnd: 1440 + bonus, duration: 0.16, type: 'triangle', volume: 0.05 });
    }, 95);
  }

  playCrash() {
    this.stopFlight();
    this.pulse({ frequency: 210, frequencyEnd: 70, duration: 0.28, type: 'sawtooth', volume: 0.08, attack: 0.005, release: 0.18 });
    window.setTimeout(() => {
      this.pulse({ frequency: 120, frequencyEnd: 55, duration: 0.24, type: 'triangle', volume: 0.055, attack: 0.005, release: 0.2 });
    }, 35);
  }

  startFlight() {
    if (!this.enabled) return;
    const ctx = this.ensure();
    if (!ctx || !this.master || ctx.state === 'suspended' || this.engineOsc) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(145, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.18);

    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(6.5, ctx.currentTime);
    lfoGain.gain.value = 8;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);

    osc.connect(gain);
    gain.connect(this.master);

    osc.start();
    lfo.start();

    this.engineOsc = osc;
    this.engineGain = gain;
    this.engineLfo = lfo;
    this.engineLfoGain = lfoGain;

    this.pulse({ frequency: 260, frequencyEnd: 420, duration: 0.18, type: 'sawtooth', volume: 0.05, attack: 0.01, release: 0.08 });
  }

  updateFlight(multiplier = 1) {
    if (!this.engineOsc || !this.ctx) return;
    const now = this.ctx.currentTime;
    const safe = Math.max(1, Number(multiplier) || 1);
    const target = Math.min(300, 145 + (safe - 1) * 18);
    const gain = Math.min(0.085, 0.04 + (safe - 1) * 0.006);
    this.engineOsc.frequency.cancelScheduledValues(now);
    this.engineOsc.frequency.linearRampToValueAtTime(target, now + 0.14);
    this.engineGain.gain.cancelScheduledValues(now);
    this.engineGain.gain.linearRampToValueAtTime(gain, now + 0.14);
  }

  stopFlight() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    if (this.engineGain) {
      try {
        this.engineGain.gain.cancelScheduledValues(now);
        this.engineGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
      } catch (e) {}
    }
    if (this.engineOsc) {
      try { this.engineOsc.stop(now + 0.22); } catch (e) {}
    }
    if (this.engineLfo) {
      try { this.engineLfo.stop(now + 0.22); } catch (e) {}
    }
    this.engineOsc = null;
    this.engineGain = null;
    this.engineLfo = null;
    this.engineLfoGain = null;
  }

  stopAll() {
    this.stopFlight();
    this.detachUnlock();
  }
}

const aviatorSound = new AviatorSoundManager();
export default aviatorSound;
